import { useAxios } from 'hooks/useAxios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { BASE_URL_V2 } from 'shared/constants';
import { UploadRequestError, UploadRequestOption } from 'rc-upload/lib/interface';

export interface Session {
  id: string;
  title: string;
  userId: string;
  projectId: string;
  messages: Messages[];
  createdAt: string;
  updatedAt: string;
  metadata?: Metadata;
}

export interface SessionsByDate {
  [date: string]: Session[];
}

export interface Project {
  id: string;
  name: string;
  category: string;
  userId: string;
  description?: string;
  isArchived?: boolean;
  contractId?: number;
  chatSessions: Session[];
  createdAt: string;
  updatedAt: string;
  contract: Contract;
}

export interface SelectedSession {
  session: Session;
  index: number;
  category: string;
  projectIndex: number;
}

export enum NodeCategory {
  SESSION = 'session',
  PROJECT = 'project'
}

export enum ProjectCategory {
  GENERAL = 'GENERAL',
  DEAL = 'DEAL',
  SUPPLIER = 'SUPPLIER',
  CLIENT = 'CLIENT'
}
export interface Contract {
  id: number;
  dealType: DealType;
  salesforceDealName: string;
  client: {
    id: number;
    name: string;
  };
  vendor: {
    id: number;
    name: string;
  };
}
export interface GroupedSessionsProject {
  id: string;
  name: string;
  category: string;
  userId: string;
  description?: string;
  isArchived?: boolean;
  contractId?: number;
  chatSessions: SessionsByDate;
  createdAt: string;
  updatedAt: string;
  contract: Contract;
}

export interface CategorizedProjects {
  GENERAL: GroupedSessionsProject[];
  DEAL: GroupedSessionsProject[];
  SUPPLIER: GroupedSessionsProject[];
  CLIENT: GroupedSessionsProject[];
}

export interface Messages {
  id: string;
  content: string;
  type: 'ai' | 'human' | 'toolUse' | 'aiThinking' | 'assistantResponse';
  chatSessionId: string;
  createdAt: string;
  updatedAt: string;

  metadata?: {
    isBookmarked: boolean;
    queryType?: string;
    files?: string[];
  };
}
export enum DealType {
  RENEWAL = 'Renewal',
  NEW_PURCHASE = 'New purchase',
  ADD_ON_PURCHASE = 'Add-on purchase'
}
export interface Metadata {
  dealType?: DealType;
  salesforceDealName?: string;
  vendor?: 'Salesforce' | string;
  client?: string;
}

interface UpdateMessageParams {
  content?: string;
  metadata: {
    isBookmarked?: boolean;
    queryType?: string;
  };
}

export interface CreateProjectParams {
  general?: {
    name: string;
    description: string;
    isArchived: boolean;
  };
  deal?: {
    contractId: number;
  };
}
interface CategoryData {
  type: 'DEAL' | 'CLIENT' | 'SUPPLIER';
  data: {
    ContractId: number | null;
  };
}
export interface QueryType {
  label: string;
  value: string;
}
export interface EmbeddedWindowState extends CategoryData {
  isEmbeddedWindowOpen: boolean;
  isDrawerOpen: boolean;
}
export interface SessionFile {
  id: string;
  source: 'salesforce' | 'user';
  filename: string;
  type: string;
  size: number;
  bucket: string;
  key: string;
  lastModified: string;
  chatSessionId: string;
  chatProjectId: string;
}

export const useChatApi = () => {
  const { axios } = useAxios(BASE_URL_V2);

  /**
   * Retrieves all projects and their associated chat sessions
   * @param {CategoryData} [categoryData] - Optional category data to filter projects by type and contract ID
   * @returns {Promise<Project[]>} A promise that resolves to an array of projects with their chat sessions
   * @throws {Error} If the API request fails
   */

  const getAllProjects = async (categoryData?: CategoryData): Promise<Project[]> => {
    try {
      const res = await axios.get(
        `chat/projects${
          categoryData ? `?category=${categoryData.type}&contractId=${categoryData.data.ContractId}` : ''
        }`
      );
      if (res?.status === 200) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch chat sessions'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };
  /**
   * Fetches vendor information from the API
   * @returns {Promise<any>} A promise that resolves to the vendor data
   */
  const getVendorInfo = async (): Promise<any> => {
    try {
      const res = await axios.get('/vendor');
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res.data?.message || 'Failed to fetch vendor information'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Retrieves all chat messages for a specific session
   * @param {string} sessionId - The ID of the chat session
   * @returns {Promise<Messages[]>} A promise that resolves to an array of chat messages
   */
  const getAllChatsBySessionId = async (sessionId: string): Promise<Messages[]> => {
    try {
      // Original API call:
      const res: any = await axios.get(`/chat/sessions/${sessionId}`);
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res.data?.message || 'Failed to fetch chat messages'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Creates a new chat session
   * @param projectId
   * @param {string} title - The title of the new session
   * @param {Metadata} [metadata] - Optional metadata for the session
   * @returns {Promise<ChatSession>} A promise that resolves to the created session
   */
  const createSession = async (projectId: string, title: string, metadata?: Metadata): Promise<Session> => {
    try {
      const res = await axios.post(`/chat/projects/${projectId}/sessions`, { title, metadata });
      if (res.status === 200 || res.status === 201) {
        return res.data;
      }
      return Promise.reject(Error(res.data?.message || 'Failed to create chat session'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Creates a new project in the specified category
   * @param {string} category - The category of the project (e.g., 'GENERAL', 'DEAL', 'SUPPLIER', 'CLIENT')
   * @param {CreateProjectParams} projectParams - Parameters for creating the project, which may include deal-specific information
   * @returns {Promise<Project>} A promise that resolves to the created project object
   * @throws {Error} If the project creation fails or if there's a network error
   */
  const createProject = async (category: string, projectParams: CreateProjectParams): Promise<Project> => {
    try {
      const res = await axios.post(`/chat/projects?category=${category}`, {
        ...projectParams
      });
      if (res.status === 200 || res.status === 201) {
        return res.data;
      }
      return Promise.reject(Error(res.data?.message || 'Failed to create chat session'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Updates a chat message content and bookmark status
   * @param {string} messageId - The ID of the message to update
   * @param {UpdateMessageParams} params - Object containing content and isBookmarked values
   * @returns {Promise<Messages>} A promise that resolves to the updated message
   */
  const updateMessageAsBookmark = async (messageId: string, params: UpdateMessageParams): Promise<Messages> => {
    try {
      const res = await axios.patch(`chat/messages/${messageId}`, params);
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res.data?.message || 'Failed to update message as bookmark'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Sends a chat query to the assistant and receives a streamed response from the LLM.
   * @param {string} id - The chat session ID.
   * @param {string} query - The user's message or prompt.
   * @param {string} token - The authentication token for the request.
   * @param {PromptModes} mode - The mode for the assistant (e.g., 'ask', 'research', 'writer').
   * @param assistantId
   * @param {AbortController} abortControllerRef - AbortController to allow cancellation of the request.
   * @param {string[]} [fileIds] - Optional array of file IDs to include in the query.
   * @param {string} [markdown] - Optional markdown content to include in the query.
   * @returns {Promise<ReadableStream>} A promise that resolves to a ReadableStream of the assistant's response.
   * @throws {Error} If the request fails or the server responds with an error.
   */
  const postChatQuery = async (
    id: string,
    query: string,
    token: string,
    assistantId: string,
    abortControllerRef: AbortController,
    fileIds?: string[]
  ): Promise<ReadableStream> => {
    try {
      const headers: HeadersInit = {
        Accept: 'application/json,text/plain,text/event-stream',
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      //all params
      console.log(id, query, token, assistantId, abortControllerRef, fileIds);
      const queryString = `${BASE_URL_V2}/chat/sessions/${id}/assistant?query=${encodeURIComponent(query)}${
        assistantId ? `&assistant=${assistantId.trim()}` : ''
      }${fileIds ? `&fileIds=${fileIds.join(',')}` : ''}`;
      const response = await fetch(queryString, {
        method: 'GET',
        headers,
        signal: abortControllerRef.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.result || `Server responded with status: ${response.status}`);
      }

      return response.body!;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Renames an existing chat session
   * @param {string} sessionId - The ID of the session to rename
   * @param {string} newTitle - The new title for the session
   * @returns {Promise<Session>} A promise that resolves to the updated session
   */
  const renameSession = async (sessionId: string, newTitle: string): Promise<Session> => {
    try {
      const res = await axios.patch(`/chat/sessions/${sessionId}`, { title: newTitle });
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res.data?.message || 'Failed to rename chat session'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Renames an existing project
   * @param {string} projectId - The ID of the project to rename
   * @param {string} newTitle - The new name for the project
   * @returns {Promise<Session>} A promise that resolves to the updated session
   * @throws {Error} If the project rename fails or if there's a network error
   */
  const renameProject = async (projectId: string, newTitle: string): Promise<Session> => {
    try {
      const res = await axios.patch(`/chat/projects/${projectId}`, { name: newTitle });
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res.data?.message || 'Failed to rename project'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Deletes a chat session
   * @param {string} sessionId - The ID of the session to delete
   * @returns {Promise<void>} A promise that resolves when the session is deleted
   */
  const deleteSession = async (sessionId: string): Promise<void> => {
    try {
      const res = await axios.delete(`/chat/sessions/${sessionId}`);
      if (res.status !== 200 && res.status !== 204) {
        return Promise.reject(Error(res.data?.message || 'Failed to delete chat session'));
      }
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const deleteProject = async (projectId: string): Promise<void> => {
    try {
      const res = await axios.delete(`/chat/projects/${projectId}`);
      if (res.status !== 200 && res.status !== 204) {
        return Promise.reject(Error(res.data?.message || 'Failed to delete chat session'));
      }
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };
  const getAllAssistants = async (): Promise<QueryType[]> => {
    try {
      const res = await axios.get(`/chat/assistant/modes`);
      console.log('res', res.data);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };
  /**
   * Retrieves all files associated with a specific chat session
   * @param projectId - The unique identifier of the chat project
   * @returns Promise<SessionFile[]> - Array of session files
   * @throws Will throw an error if the API request fails
   */
  const getAllFilesBySessionId = async (projectId: string): Promise<SessionFile[]> => {
    try {
      const res = await axios.get(`/chat/projects/${projectId}/files`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };
  /**
   * Uploads a file to a specific chat session with progress tracking
   * @param options - Upload request options containing file and callback functions
   * @param options.file - The file to upload
   * @param options.onSuccess - Callback function called on successful upload
   * @param options.onError - Callback function called on upload error
   * @param options.onProgress - Callback function for upload progress updates
   * @param projectId - The unique identifier of the chat project
   * @returns Promise<SessionFile[]> - Array of uploaded session files
   * @throws Will throw an error if the upload fails
   */
  const uploadFileToSession = async (options: UploadRequestOption, projectId: string): Promise<SessionFile[]> => {
    const { file, onSuccess, onError, onProgress } = options;
    const formData = new FormData();
    formData.append('files', file);

    try {
      const res = await axios.postForm(`/chat/projects/${projectId}/files`, formData, {
        transformRequest: () => formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          console.log('progressEvent', progressEvent);
          onProgress?.({ percent: Math.round(progressEvent.progress || 0) * 100 });
        }
      });
      onSuccess?.(res.data);
      return res.data;
    } catch (e) {
      onError?.(e as UploadRequestError);
      AxiosErrorHandler(e);
      throw e;
    }
  };
  /**
   * Deletes a specific file from a chat session
   * @param fileId - The unique identifier of the file to delete
   * @param projectId - The unique identifier of the chat project
   * @returns Promise<void> - Resolves when file is successfully deleted
   * @throws Will throw an error if the deletion fails
   */
  const deleteFileFromSession = async (fileId: string, projectId: string): Promise<void> => {
    try {
      const res = await axios.delete(`/chat/projects/${projectId}/files/${fileId}`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };
  /**
   * Stages multiple files for a chat session, categorizing them as successful or failed
   * @param projectId - The unique identifier of the chat project
   * @param stagedFiles - Array of session files to be staged
   * @returns Promise<{success: SessionFile[], failed: SessionFile[]}> - Object containing successfully staged files and failed files
   * @throws Will throw an error if the staging operation fails
   */
  const stageFilesForSession = async (
    projectId: string,
    stagedFiles: SessionFile[]
  ): Promise<{ success: SessionFile[]; failed: SessionFile[] }> => {
    const stagedFilesIds = stagedFiles.map((file) => file.id);
    try {
      const res = await axios.post(`/chat/projects/${projectId}/files/stage`, stagedFilesIds);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };
  const saveReport = async (sessionId: string, reportType: string, reportContent: string): Promise<void> => {
    const requestBody: any = { reports: {} };
    requestBody.reports[reportType] = reportContent;
    try {
      const res = await axios.patch(`/chat/sessions/${sessionId}`, requestBody);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };
  //add an api to submit feedback for a message
  const submitMessageFeedback = async (
    sessionId: string,
    messageId: string,
    status: string,
    textFeedback?: string
  ): Promise<Messages> => {
    try {
      const res = await axios.patch(`/chat/sessions/${sessionId}/messages/${messageId}`, {
        responseMetadata: { feedbackRating: status, feedbackComment: textFeedback }
      });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };
  return {
    getAllProjects,
    getAllChatsBySessionId,
    createSession,
    createProject,
    updateMessageAsBookmark,
    postChatQuery,
    getVendorInfo,
    renameSession,
    renameProject,
    deleteSession,
    deleteProject,
    getAllAssistants,
    getAllFilesBySessionId,
    uploadFileToSession,
    deleteFileFromSession,
    stageFilesForSession,
    saveReport,
    submitMessageFeedback
  };
};
